import { useTranslation } from "react-i18next";

import contributorRoles from "astrid-firestore/src/api/articles/constants/contributorRoles";
import secondsToDuration from "astrid-firestore/src/api/articles/utils/secondsToDuration";

import useHasRight from "../../authorization/hooks/useHasRight";
import useDeliveryTypeTranslations from "../../distribution/hooks/useDeliveryTypeTranslations";
import { getOrganizationPath } from "../../routing/utils/getOrganizationPath";

import useArticleChannelMetadata from "./useArticleChannelMetadata";
import useArticleMetadata from "./useArticleMetadata";
import useArticleStatusTranslations from "./useArticleStatusTranslations";

export default function useArticleMetadataValues() {
	const { t } = useTranslation();
	const hasRight = useHasRight();

	const statusTranslations = useArticleStatusTranslations();
	const deliveryTypeTranslations = useDeliveryTypeTranslations();

	const metadata = [...useArticleMetadata({}), ...useArticleChannelMetadata()];

	return (data) => {
		data = data || {};

		const values = Object.fromEntries(
			metadata
				.filter(({ format }) => format)
				.map(({ name, label, format }) => [name, { label, value: format(data) }]),
		);

		return {
			...values,
			serie: {
				...values.serie,
				href: data.serie && hasRight("articles.view") ? getOrganizationPath(`/series/${data.serie.id}`) : null,
			},
			status: { label: t("status", "Status"), value: statusTranslations[data.status] },
			isbn: { label: t("isbn", "ISBN"), value: data.isbn },
			announcementDate: { label: t("announcementDate", "Announcement date"), value: data.announcementDate },
			releaseDate: { label: t("releaseDate", "Release date"), value: data.releaseDate },
			takedownDate: { label: t("takedownDate", "Takedown date"), value: data.takedownDate },
			authors: { label: t("authors", "Authors"), value: contributorNames(data, contributorRoles.AUTHOR) },
			translators: {
				label: t("translators", "Translators"),
				value: contributorNames(data, contributorRoles.TRANSLATOR),
			},
			narrators: { label: t("narrators", "Narrators"), value: contributorNames(data, contributorRoles.NARRATOR) },
			duration: {
				label: t("duration", "Duration"),
				value: data.duration ? secondsToDuration(data.duration, { padHours: 2 }) : null,
			},
			epubVersion: { label: t("epubVersion", "Epub version"), value: data.epubVersion },
			accessibility: { label: t("accessibility", "Accessibility"), value: data.accessibility?.join(", ") },
			hazardsOrWarnings: {
				label: t("hazardsOrWarnings", "Hazards or warnings"),
				value: data.hazardsOrWarnings?.join(", "),
			},
			deliveryTypes: {
				label: t("deliveryTypes", "Delivery types"),
				value: data.deliveryTypes?.map((deliveryType) => deliveryTypeTranslations[deliveryType]).join(", "),
			},
		};
	};
}

function contributorNames(data, role) {
	return data.contributors
		?.filter((contributor) => contributor.role === role)
		.map((contributor) => `${contributor.firstName} ${contributor.lastName}`.trim())
		.filter(Boolean)
		.join(", ");
}
